import { inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@unified-ui/environments';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { AuthSession, decodeJWT, JWT, TokenProvider } from 'aws-amplify/auth';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of, switchMap, take, tap } from 'rxjs';
import { TOKEN_TYPE, TokensInterface } from './types/tokens.type';
/**
 * Init tokens, save them in local storage, provide methods to access tokens and tokensReady,
 * redirects to login page if needed
 */
@Injectable({
    providedIn: 'root'
})
export class AuthTokensService {
    private _tokensReady$ = signal<boolean>(false);
    public readonly tokensReady$: Signal<boolean> = this._tokensReady$;
    private _cookieService = inject(CookieService);
    private _router = inject(Router);
    private _amplifyConfig = environment.aws.amplify as ResourcesConfig;

    /**
     * Init tokens and call the setters or the login page
     * @returns Promise resolved for main.ts
     */
    public initTokens(): Promise<void> {
        return new Promise(resolve => {
            this._getAuthTokens()
                .pipe(take(1))
                .subscribe({
                    next: tokens => {
                        if (tokens) {
                            this.configureAmplify(tokens);
                            this._saveTokensInLocalStorage(tokens);
                            this._tokensReady$.set(true);
                        } else {
                            this._tokensReady$.set(false);
                            this.loginNavigation();
                        }
                        resolve();
                    },
                    error: err => {
                        console.error('Error to init tokens', err);
                        this._tokensReady$.set(false);
                        this.loginNavigation();
                        resolve();
                    }
                });
        });
    }

    /**
     * Retrieves the access token from local storage.
     * @returns {string} - The access token.
     */
    public getAuthToken(token: TOKEN_TYPE): string {
        const { prefix, subLastAuthUser } = this._getLocalStoragePrefix();
        return localStorage.getItem(`${prefix}.${subLastAuthUser}.${token}`) ?? '';
    }

    /**
     * Update tokens in storage, cookies and store parameters when a new session is provided.
     * @param {AuthSession} session - The new auth session from amplify.
     */
    public updateAuthTokens(session: AuthSession): void {
        // set readiness to false
        this._tokensReady$.set(false);
        // organize the new tokens
        const newTokens = {
            accessToken: session.tokens?.accessToken.toString() ?? '',
            idToken: session.tokens?.idToken?.toString() ?? '',
            refreshToken: this.getAuthToken(TOKEN_TYPE.REFRESH) ?? '',
            orgId: this.getAuthToken(TOKEN_TYPE.ORG_ID) ?? '',
            orgList: this.getAuthToken(TOKEN_TYPE.ORG_LIST) ?? '',
            uiAnalytics: this.getAuthToken(TOKEN_TYPE.UI_ANALYTICS) === 'true' ? true : false
        };
        // save the new tokens in amplify and storage
        this.configureAmplify(newTokens);
        this._saveTokensInLocalStorage(newTokens);
        // check if the cookies are already set with the token to update
        if (this._hasCookiesAccessToken(newTokens.accessToken)) {
            this._setTokensInCookies(newTokens);
        }
        // update for the subscribers
        this._tokensReady$.set(true);
    }

    /**
     * Retrieves the user selected org id from local storage.
     *
     * @returns {string} - The access token.
     */
    public getOrgId(): string {
        const clientId = this._amplifyConfig.Auth?.Cognito?.userPoolClientId || '';
        const prefix = `CognitoIdentityServiceProvider.${clientId}`;
        const subLastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`) ?? '';
        return localStorage.getItem(`${prefix}.${subLastAuthUser}.${TOKEN_TYPE.ORG_ID}`) ?? '';
    }

    /**
     * Retrieves the user org list from local storage.
     * @returns {string} - The org list.
     */
    public getOrgList(): string {
        const clientId = this._amplifyConfig.Auth?.Cognito?.userPoolClientId || '';
        const prefix = `CognitoIdentityServiceProvider.${clientId}`;
        const subLastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`) ?? '';
        return localStorage.getItem(`${prefix}.${subLastAuthUser}.${TOKEN_TYPE.ORG_LIST}`) ?? '';
    }

    /**
     * Redirects to login url
     */
    public loginNavigation() {
        window.location.href = `${environment.loginBaseUrl}/logout?returnUrl=${environment.returnUrl}`;
    }

    /**
     * Get tokens from preferentially from cookies or url
     * @returns Tokens or null
     */
    private _getAuthTokens(): Observable<TokensInterface | null> {
        return this._getTokensFromCookies().pipe(
            switchMap(tokensFromCookies => {
                if (tokensFromCookies) {
                    return of(tokensFromCookies);
                }
                return this._getTokensFromUrlOrLocalStore();
            })
        );
    }

    /**
     * Checks cookies service for tokens
     * @returns tokens or null
     */
    private _getTokensFromCookies(): Observable<null | TokensInterface> {
        const tokensFromCookies: TokensInterface = {
            accessToken: this._cookieService.get(TOKEN_TYPE.ACCESS),
            idToken: this._cookieService.get(TOKEN_TYPE.ID),
            refreshToken: this._cookieService.get(TOKEN_TYPE.REFRESH),
            orgId: this._cookieService.get(TOKEN_TYPE.ORG_ID),
            orgList: this._cookieService.get(TOKEN_TYPE.ORG_LIST),
            uiAnalytics: this._cookieService.get(TOKEN_TYPE.UI_ANALYTICS) === 'true'
        };

        const hasAllRequiredTokensFromCookies = Object.values(tokensFromCookies).every(token => token !== '' && token !== undefined);
        return hasAllRequiredTokensFromCookies ? of(tokensFromCookies) : of(null);
    }

    /**
     * Checks the url or local store for tokens
     * @returns Tokens or null
     */
    private _getTokensFromUrlOrLocalStore(): Observable<TokensInterface | null> {
        const searchParams = new URLSearchParams(window.location.search);
        const tokensFromUrl: TokensInterface = {
            accessToken: searchParams.get(TOKEN_TYPE.ACCESS) as string,
            idToken: searchParams.get(TOKEN_TYPE.ID) as string,
            refreshToken: searchParams.get(TOKEN_TYPE.REFRESH) as string,
            orgId: searchParams.get(TOKEN_TYPE.ORG_ID) as string,
            orgList: searchParams.get(TOKEN_TYPE.ORG_LIST) as string,
            uiAnalytics: searchParams.get(TOKEN_TYPE.UI_ANALYTICS) === 'true'
        };

        const tokensFromLocalStore: TokensInterface = {
            accessToken: this.getAuthToken(TOKEN_TYPE.ACCESS),
            idToken: this.getAuthToken(TOKEN_TYPE.ID),
            refreshToken: this.getAuthToken(TOKEN_TYPE.REFRESH),
            orgId: this.getAuthToken(TOKEN_TYPE.ORG_ID),
            orgList: this.getAuthToken(TOKEN_TYPE.ORG_LIST),
            uiAnalytics: this.getAuthToken(TOKEN_TYPE.UI_ANALYTICS) === 'true' ? true : false
        };

        const hasAllRequiredTokensFromUrl = Object.values(tokensFromUrl).every(token => token !== null && token !== undefined && token !== '');
        const hasAllRequiredTokensFromLocal = Object.values(tokensFromLocalStore).every(token => token !== null && token !== undefined && token !== '');

        if (hasAllRequiredTokensFromUrl) {
            return of(tokensFromUrl).pipe(
                tap(() => {
                    this._router.navigate([], {
                        queryParams: {},
                        replaceUrl: true
                    });
                })
            );
        } else if (hasAllRequiredTokensFromLocal) {
            return of(tokensFromLocalStore);
        } else {
            return of(null);
        }
    }

    /**
     * Build the token provider for amplify
     * @param tokens tokens to provide
     * @returns custom token provider
     */
    private amplifyTokenProvider = (tokens: TokensInterface): TokenProvider => {
        return {
            getTokens: async () => {
                return {
                    accessToken: decodeJWT(tokens.accessToken),
                    idToken: decodeJWT(tokens.idToken),
                    refreshToken: tokens.refreshToken
                };
            }
        };
    };

    /**
     * Configures Amplify with environment config + token provider
     * @param tokens tokens to provide
     */
    private configureAmplify(tokens: TokensInterface) {
        Amplify.configure(environment.aws.amplify as ResourcesConfig, {
            Auth: {
                tokenProvider: this.amplifyTokenProvider(tokens)
            }
        });
    }

    /**
     * Saves the provided tokens in local storage. They are required for cognito to work.
     * @param {TokensInterface} tokens - The tokens to save.
     */
    private _saveTokensInLocalStorage(tokens: TokensInterface): void {
        const token: JWT = decodeJWT(tokens.accessToken);
        const { sub, client_id, iat } = token.payload;
        const prefix = `CognitoIdentityServiceProvider.${client_id}`;
        const clockDrift = (iat ?? 0) - Math.floor(Date.now() / 1000);
        localStorage.setItem(`${prefix}.LastAuthUser`, sub || '');
        localStorage.setItem(`${prefix}.${sub}.clockDrift`, clockDrift.toString());
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.ACCESS}`, tokens.accessToken);
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.ID}`, tokens.idToken);
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.REFRESH}`, tokens.refreshToken);
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.ORG_ID}`, tokens.orgId);
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.ORG_LIST}`, tokens.orgList);
        localStorage.setItem(`${prefix}.${sub}.${TOKEN_TYPE.UI_ANALYTICS}`, tokens.uiAnalytics ? 'true' : 'false');
    }

    /**
     * Updates cookies with tokens
     * @param tokens tokens to set in cookies
     */
    private _setTokensInCookies(tokens: TokensInterface): void {
        this._cookieService.set(TOKEN_TYPE.ACCESS, tokens?.accessToken);
        this._cookieService.set(TOKEN_TYPE.ID, tokens?.idToken);
        this._cookieService.set(TOKEN_TYPE.REFRESH, tokens.refreshToken);
        this._cookieService.set(TOKEN_TYPE.ORG_ID, tokens.orgId);
        this._cookieService.set(TOKEN_TYPE.ORG_LIST, tokens.orgList);
        this._cookieService.set(TOKEN_TYPE.UI_ANALYTICS, tokens.uiAnalytics.toString());
    }

    /**
     * @returns Prefixes of saved tokens in storage
     */
    private _getLocalStoragePrefix(): { prefix: string; subLastAuthUser: string } {
        const clientId = this._amplifyConfig.Auth?.Cognito?.userPoolClientId || '';
        const prefix = `CognitoIdentityServiceProvider.${clientId}`;
        const subLastAuthUser = localStorage.getItem(`${prefix}.LastAuthUser`) ?? '';
        return { prefix, subLastAuthUser };
    }

    /**
     * Check if the token from cookies is updated
     * @returns if exists and it is different from the updated token
     */
    private _hasCookiesAccessToken(updatedToken: string): boolean {
        const accessTokenFromCookies = this._cookieService.get(TOKEN_TYPE.ACCESS);
        return !!accessTokenFromCookies && updatedToken !== accessTokenFromCookies;
    }
}
